import React from 'react';
import { Link } from 'react-router-dom';
import { TrendUp, TrendDown, Minus, Question } from '@phosphor-icons/react';
import TrendSparkline from './TrendSparkline';
import Avatar from './Avatar';

/**
 * Direction is a status, not a series identity, so each one ships with an icon
 * and a word as well as a colour — the direction is never carried by colour
 * alone. Colours are the app's existing semantic three.
 */
const DIRECTIONS = [
  {
    key: 'declining',
    label: 'Declining',
    blurb: 'Down 10+ points across the range',
    color: '#B0200C',
    Icon: TrendDown,
  },
  {
    key: 'steady',
    label: 'Holding steady',
    blurb: 'Within 10 points across the range',
    color: '#6B7280',
    Icon: Minus,
  },
  {
    key: 'improving',
    label: 'Improving',
    blurb: 'Up 10+ points across the range',
    color: '#1E6C30',
    Icon: TrendUp,
  },
  {
    key: 'insufficient',
    label: 'Not enough data',
    blurb: 'Only one scored month in this range — widen it to see a direction',
    color: '#9CA3AF',
    Icon: Question,
  },
];

function ChangePill({ change, color }) {
  const sign = change > 0 ? '+' : '';
  return (
    <span className="text-sm font-bold tabular-nums" style={{ color }}>
      {sign}{change}
    </span>
  );
}

function ClinicianRow({ entry, color, domain, clinicId, clinicName }) {
  const to = `/clinicians/${entry.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinicName)}`;

  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
      <Avatar src={entry.image_url} name={entry.clinician_name} size="sm" />
      <div className="min-w-0 flex-1">
        <Link to={to} className="text-primary font-medium hover:underline text-sm truncate block">
          {entry.clinician_name}
        </Link>
        <p className="text-xs text-gray-500">
          {entry.points.length > 1
            ? `${entry.first_score} → ${entry.latest_score} over ${entry.points.length} months`
            : `${entry.latest_score} · 1 scored month`}
        </p>
      </div>
      <TrendSparkline points={entry.points} color={color} domain={domain} />
      <div className="w-12 text-right shrink-0">
        {entry.points.length > 1
          ? <ChangePill change={entry.change} color={color} />
          : <span className="text-sm text-gray-300">—</span>}
      </div>
    </div>
  );
}

/**
 * Where every therapist's CScore is heading over a span of months.
 *
 * Small multiples rather than one line per therapist: a clinic carries far
 * more clinicians than a categorical palette has distinct hues, so a single
 * overlaid chart would be unreadable spaghetti and would have to invent
 * colours past the eighth series. One sparkline each, grouped by direction,
 * answers "who is slipping" at a glance and scales to any roster size.
 */
export default function TrendPanel({ series, clinicId, clinicName, loading }) {
  if (loading) {
    return <div className="py-8 text-center text-gray-400 text-sm">Loading trends...</div>;
  }

  if (!series || series.length === 0) {
    return <p className="text-sm text-gray-500 py-4">No scored months in this range.</p>;
  }

  // One shared y-domain across every sparkline, so the shapes are comparable
  // between clinicians rather than each being scaled to its own range.
  const allScores = series.flatMap(s => s.points.map(p => p.score));
  const min = Math.min(...allScores);
  const max = Math.max(...allScores);
  const domain = [min, max];

  const grouped = DIRECTIONS.map(direction => ({
    ...direction,
    entries: series.filter(s => s.direction === direction.key),
  })).filter(group => group.entries.length > 0);

  return (
    <div className="space-y-6">
      {grouped.map(({ key, label, blurb, color, Icon, entries }) => (
        <div key={key}>
          <div className="flex items-center gap-2 mb-1">
            <Icon size={18} weight="bold" style={{ color }} />
            <h4 className="text-sm font-bold text-text">{label}</h4>
            <span className="text-xs text-gray-400">
              {entries.length} clinician{entries.length === 1 ? '' : 's'}
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-1">{blurb}</p>
          <div>
            {entries.map(entry => (
              <ClinicianRow
                key={entry.clinician_id}
                entry={entry}
                color={color}
                domain={domain}
                clinicId={clinicId}
                clinicName={clinicName}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
